import { type Capability, capabilitiesForRole, isReadOnlyCapability } from '@emr/shared';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';

import { useSession } from '../auth/sessionContext.ts';
import { DataTable, type DataTableColumn } from '../components/DataTable.tsx';
import { PageHeading, Panel } from '../components/primitives.tsx';

/**
 * What this person's role permits, read from `@emr/shared` rather than described.
 *
 * `capabilitiesForRole` is exported by that module specifically "for the UI, which needs to hide
 * what it must not offer, and for the members-management screen, which has to explain what a role
 * means before an administrator assigns it". This is that screen's first, smallest form.
 *
 * It also carries no PHI and needs no API call, which is why it was originally the page the
 * responsive table/cards pattern was demonstrated on directly (`components/DataTable.tsx`'s own
 * header comment: "generalised from `AccessPage.tsx`'s `CapabilityTable`/`CapabilityCards`"). Now
 * migrated to the generalised `DataTable` itself, so this is no longer a second, parallel
 * implementation of the same table/cards mechanism — the bespoke `CapabilityTable`/`CapabilityCards`
 * pair this file used to define is gone; `DataTable.tsx` is the one place that logic lives.
 */

/**
 * The `access.json` translation key for each capability's "what it allows" sentence, under that
 * namespace's `capabilities` object. Keyed by capability id rather than embedding the sentence
 * directly, so the sentence itself can live in `locales/*\/access.json` instead of here — but the
 * `Record<Capability, string>` type is still the same forcing function the original hardcoded copy
 * had: a capability added to the shared union without an entry here fails the build rather than
 * rendering a blank cell. The colon in a capability id (`patient:read`) is avoided in the
 * translation key itself (`patientRead`) because i18next's default namespace separator is also
 * `:`, and a key containing one would be misparsed as `namespace:key`.
 */
const CAPABILITY_COPY_KEY: Record<Capability, string> = {
  'patient:read': 'patientRead',
  'patient:write': 'patientWrite',
  'chart:read': 'chartRead',
  'chart:write': 'chartWrite',
  'chart:prescribe': 'chartPrescribe',
  'note:sign': 'noteSign',
  'note:cosign': 'noteCosign',
  'schedule:manage': 'scheduleManage',
  'billing:manage': 'billingManage',
  'org:manage_members': 'orgManageMembers',
  'org:manage_settings': 'orgManageSettings',
  'audit:read': 'auditRead',
  'chart:break_glass': 'chartBreakGlass',
  'chart:disclose': 'chartDisclose',
  'platform:manage_practices': 'platformManagePractices',
  'platform:read_audit_log': 'platformReadAuditLog',
  'org:read_children': 'orgReadChildren',
};

function scopeLabel(capability: Capability, t: TFunction<'access'>): string {
  return isReadOnlyCapability(capability) ? t('scope.readOnly') : t('scope.changesData');
}

/**
 * `min-width: 34rem` is `DataTable`'s own default, matching this table's original, measured
 * declaration exactly — three columns whose longest content is a capability name plus a scope word.
 * The description column wraps rather than pushing the row wider: an unbounded cell that grows the
 * table is how the last column, usually the action buttons, ends up scrolled out of sight while
 * nothing appears to be wrong (`docs/comparison-emrx.md` §7).
 *
 * Built as a function of `t` rather than a module-level constant: `t` only exists once
 * `useTranslation` has run inside a component, and column headers need to re-render when the
 * active language changes, which a value computed once at module load could never do.
 */
function columnsFor(t: TFunction<'access'>): readonly DataTableColumn<Capability>[] {
  return [
    {
      key: 'capability',
      header: t('columns.capability'),
      cell: (capability) => capability,
      wrap: 'anywhere',
      maxWidthCh: 16,
      cellClassName: 'font-mono',
    },
    {
      key: 'allows',
      header: t('columns.allows'),
      cell: (capability) => t(`capabilities.${CAPABILITY_COPY_KEY[capability]}`),
      wrap: 'wrap',
    },
    {
      key: 'scope',
      header: t('columns.scope'),
      cell: (capability) => scopeLabel(capability, t),
      wrap: 'nowrap',
      cellClassName: 'text-ink-muted',
    },
  ];
}

export function AccessPage() {
  const { t } = useTranslation('access');
  const session = useSession();
  const capabilities = capabilitiesForRole(session.role);
  const columns = columnsFor(t);

  return (
    <div className="flex flex-col gap-4">
      <PageHeading title={t('heading')} helpTopicId="your-access" />

      <p className="m-0 text-sm text-ink-muted">{t('scopingNote')}</p>

      {/* A plain wrapping `div` carrying `data-help-id`, rather than the attribute on `Panel`
          itself: `Panel`'s own prop type does not forward arbitrary attributes to its underlying
          `<section>`, and widening a shared primitive's props just for this one call site is a
          bigger change than this needs. */}
      <div data-help-id="capability-panel">
        <Panel>
          <DataTable
            caption={t('tableCaption')}
            columns={columns}
            rows={capabilities}
            rowKey={(capability) => capability}
            emptyMessage={t('emptyMessage', { role: session.role })}
          />
        </Panel>
      </div>
    </div>
  );
}
